import { Show, type VoidComponent } from "solid-js";
import { isNil } from "lodash-es";
import DetailModal from "../../../../../components/modal/DetailModal";
import { useTranslation } from "../../../../../state/i18n";
import { useCurrentModal } from "../../../../../components/modal/ModalManager";
import { TextButton } from "../../../../../components/button/Button";
import { formatDate } from "../../../../../components/formatting/FormatDate";
import { CUSTOMER_DELETION_DELAY_DAYS } from "../../../../../constants";
import { calculateRemainingDays } from "../../../../../utils/planUtil";
import { condition } from "../../../../../utils/solidUtils";
import { seconds } from "../../../../../utils/timeUtil";
import CancellationInfoItem from "./CancellationInfoItem";

type SubscriptionCancelledModalProps = {
  cycleEnd?: number;
};

const calculateDeletionDate = (cycleEnd: number): Date => {
  const deletionDate = new Date(seconds(cycleEnd));
  deletionDate.setUTCDate(deletionDate.getUTCDate() + CUSTOMER_DELETION_DELAY_DAYS);
  return deletionDate;
};

const SubscriptionCancelledModal: VoidComponent<SubscriptionCancelledModalProps> = (props) => {
  const { t } = useTranslation();
  const { close } = useCurrentModal();

  const knownCycleEnd = (): number | null | undefined => condition(props.cycleEnd, (cycleEnd) => !isNil(cycleEnd));

  return (
    <DetailModal
      fullHeight
      title={t(($) => $.CANCELLED_MODAL_TITLE)}
      content={() => (
        <div class="flex flex-col gap-3">
          <div class="flex flex-col gap-4">
            <p class="text-base-semibold text-secondary">{t(($) => $.CANCELLED_MODAL_CONTENT_TITLE)}</p>
            <Show when={knownCycleEnd()}>
              {(cycleEnd) => (
                <CancellationInfoItem
                  icon="calendar"
                  title={t(($) => $.CANCELLED_MODAL_DAYS_LEFT_TITLE, { count: calculateRemainingDays(cycleEnd()) })}
                  description={t(($) => $.CANCELLED_MODAL_DAYS_LEFT_DESCRIPTION)}
                />
              )}
            </Show>
            <CancellationInfoItem
              icon="download"
              title={t(($) => $.CANCELLED_MODAL_EXPORT_TITLE)}
              description={t(($) => $.CANCELLED_MODAL_EXPORT_DESCRIPTION, { days: CUSTOMER_DELETION_DELAY_DAYS })}
            />
            <Show when={knownCycleEnd()}>
              {(cycleEnd) => (
                <CancellationInfoItem
                  icon="trash"
                  title={t(($) => $.CANCELLED_MODAL_DELETION_TITLE, {
                    deleteOn: formatDate(calculateDeletionDate(cycleEnd()), true),
                  })}
                  description={t(($) => $.CANCELLED_MODAL_DELETION_DESCRIPTION)}
                />
              )}
            </Show>
          </div>
        </div>
      )}
      footer={
        <div class="flex w-full justify-end gap-2">
          <TextButton
            text={t(($) => $.CANCELLED_MODAL_BACK_TO_OVERVIEW_TITLE)}
            variant="primary"
            onClick={() => close()}
          />
        </div>
      }
    />
  );
};

export default SubscriptionCancelledModal;
